import { BuildingHistoryResItem } from '@gateway.Building'
import {
  Card,
  CardBody,
  CardHeader,
  CardSubtitle,
  CardTitle,
  GridLayout,
  GridLayoutItem
} from '@progress/kendo-react-layout'
import { Tooltip } from '@progress/kendo-react-tooltip'
import dayjs from 'dayjs'
import { FC } from 'react'
import Constants from '../../../data/Constants.json'
import { useAppSelector } from '../../../stores'

export interface BuildingHistoryItemProps {
  data: BuildingHistoryResItem
  skip: number
  itemIndex: number
}

export const BuildingHistoryItem: FC<BuildingHistoryItemProps> = ({
  data,
  skip,
  itemIndex
}) => {
  const buildingHistoryList = useAppSelector(
    (state) => state.buildingHistory.buildingHistoryList
  )

  const getItemColor = (key: string) => {
    if (skip + itemIndex + 1 === buildingHistoryList.length) {
      return 'gray'
    }
    const itemkey = key as keyof BuildingHistoryResItem
    if (data[itemkey] !== buildingHistoryList[skip + itemIndex + 1][itemkey]) {
      return '#D0611E'
    } else {
      return 'gray'
    }
  }

  const convertFlagValue = (category: string, val?: number) => {
    if (category === undefined || val === undefined) {
      return ''
    }
    if (
      category === 'piling' ||
      category === 'seismicIsolation' ||
      category === 'buildingServicesWorkFlg' ||
      category === 'coreTopFlg' ||
      category === 'choiceAndConcentrationTarget'
    ) {
      return Constants.presenceFlag.find((item) => item.code === String(val))
        ?.name
    }
    if (category === 'topDown') {
      return Constants.topdownFlag.find((item) => item.code === String(val))
        ?.name
    }
    return ''
  }

  return (
    <>
      <Tooltip position='right' style={{ zIndex: 100000 }}>
        <Card orientation="horizontal" style={{ width: '100%', margin: 'auto' }}>
          <CardHeader style={{ padding: '1% 1%', width: '15%' }}>
            <CardTitle style={{ marginTop: '0px' }}>
              <span
                style={{
                  fontSize: '1.25em',
                  fontWeight: 'bold',
                  color: getItemColor('branchNm')
                }}
              >
                {data.branchNm}
              </span>
            </CardTitle>
            <CardSubtitle style={{ marginTop: '0px' }}>
              <span
                style={{
                  fontSize: '1em',
                  fontWeight: 'bold',
                  color: getItemColor('procurementNo')
                }}
              >
                {data.procurementNo}
              </span>
              <span
                style={{ fontSize: '1em', fontWeight: 'bold', color: 'gray' }}
              >
                -
              </span>
              <span
                style={{
                  fontSize: '1em',
                  fontWeight: 'bold',
                  color: getItemColor('subNo')
                }}
              >
                {data.subNo}
              </span>
            </CardSubtitle>
            <CardSubtitle style={{ marginTop: '0px' }}>
              <span
                style={{
                  fontSize: '1em',
                  fontWeight: 'bold',
                  color: getItemColor('buildingNo')
                }}
              >
                {data.buildingNo}
              </span>
            </CardSubtitle>
            <CardSubtitle style={{ marginTop: '0px' }}>
              <span
                style={{
                  fontSize: '1.5em',
                  fontWeight: 'bold',
                  color: getItemColor('evaluationValue')
                }}
              >
                {data.evaluationValue}
              </span>
            </CardSubtitle>
            <CardSubtitle style={{ marginTop: '0px' }}>
              <span
                style={{
                  fontSize: '1.5em',
                  fontWeight: 'bold',
                  color: 'gray'
                }}
              >
                {'選択と集中：'}
              </span>
              <span
                style={{
                  fontSize: '1.5em',
                  fontWeight: 'bold',
                  color: getItemColor('choiceAndConcentrationTarget')
                }}
              >
                {convertFlagValue('choiceAndConcentrationTarget', data.choiceAndConcentrationTarget) ?? ''}
              </span>
            </CardSubtitle>
            <CardSubtitle style={{ marginTop: '0px' }}>
              <span
                style={{
                  fontSize: '0.8em',
                  fontWeight: 'bold',
                  color: getItemColor('historyAddDtm')
                }}
              >
                {dayjs(data.historyAddDtm).format('YYYY/MM/DD HH:mm')}
              </span>
            </CardSubtitle>
            <CardSubtitle style={{ marginTop: '0px' }}>
              <span style={{ whiteSpace: 'nowrap' }}>
                <span
                  style={{
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    color: getItemColor('updUserKbn')
                  }}
                >
                  {data.updUserKbn}
                </span>
                <span
                  style={{
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    color: 'gray'
                  }}
                >
                  /
                </span>
                <span
                  style={{
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    color: getItemColor('updUserNm'),
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={data.updUserNm ?? ''}
                >
                  {data.updUserNm}
                </span>
              </span>
            </CardSubtitle>
          </CardHeader>
          <CardBody style={{ padding: '1% 1%', width: '65%' }}>
            <GridLayout
              gap={{ cols: 10 }}
              cols={[{ width: '40%' }, { width: '40%' }, { width: '20%' }]}
            >
              <GridLayoutItem
                col={1}
                style={{ marginLeft: 10, marginRight: 10 }}
              >
                <GridLayout>
                  <GridLayoutItem
                    col={1}
                    colSpan={3}
                    row={1}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'normal',
                        fontSize: '1.0em',
                        color: getItemColor('constructionNm')
                      }}
                      title={data.constructionNm ?? ''}
                    >
                      {data.constructionNm}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={1}
                    colSpan={3}
                    row={2}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('constructionSubjectCd')
                      }}
                    >
                      {data.constructionSubjectCd}{' '}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('procurementStsStr')
                      }}
                    >
                      {data.procurementStsStr}{' '}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('obtainHistoryStr')
                      }}
                    >
                      {data.obtainHistoryStr}{' '}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('constructionFormCd')
                      }}
                    >
                      {data.constructionFormCd}{' '}
                    </span>
                    {
                        data.ourCompanyEquityRatio !== null && data.ourCompanyEquityRatio !== undefined
                          ? <>
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8em',
                                  color: `${getItemColor('ourCompanyEquityRatio')}`
                                }}
                              >
                                {data.ourCompanyEquityRatio}
                              </span>
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8em',
                                  color: 'gray'
                                }}
                              >
                                %{' '}
                              </span>
                            </>
                          : <></>
                      }
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('effortLevelStr')
                      }}
                    >
                      {data.effortLevelStr}{' '}
                    </span>
                    {
                        data.coreTopFlg !== null
                          ? <>
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8em',
                                  color: 'gray'
                                }}
                              >
                                {'コアトップ案件:'}
                              </span>
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8em',
                                  color: `${getItemColor('coreTopFlg')}`
                                }}
                              >
                                {convertFlagValue('coreTopFlg', data.coreTopFlg) ?? ''}{' '}
                              </span>
                            </>
                          : <></>
                      }
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('obtainFy')
                      }}
                    >
                      {data.obtainFy}{' '}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('prefecture'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden'
                      }}
                      title={data.prefecture ?? ''}
                    >
                      {data.prefecture}{' '}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('city'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden'
                      }}
                      title={data.city ?? ''}
                    >
                      {data.city}{' '}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={1}
                    colSpan={3}
                    row={3}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <>
                      {
                        data.totalCost !== null && data.totalCost !== undefined
                          ? <>
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8em',
                                  color: 'gray'
                                }}
                              >
                                {data.totalCost !== null && data.totalCost !== undefined ? '総工事金額（百万）:￥' : ''}
                              </span>
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8em',
                                  color: `${getItemColor('totalCost')}`,
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden'
                                }}
                                title={
                                  data.totalCost !== null && data.totalCost !== undefined
                                    ? (data.totalCost / 1000000).toLocaleString()
                                    : ''
                                }
                              >
                                {
                                  data.totalCost !== null && data.totalCost !== undefined
                                    ? (data.totalCost / 1000000).toLocaleString()
                                    : ''
                                }{' '}
                              </span>
                            </>
                          : <></>
                      }
                      {
                        data.ourCompanyTotalCost !== null && data.ourCompanyTotalCost !== undefined
                          ? <>
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8em',
                                  color: 'gray'
                                }}
                              >
                                {data.ourCompanyTotalCost !== null && data.ourCompanyTotalCost !== undefined ? '当社概算工事金額（百万）:￥' : ''}
                              </span>
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8em',
                                  color: `${getItemColor('ourCompanyTotalCost')}`,
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden'
                                }}
                                title={
                                  data.ourCompanyTotalCost !== null && data.ourCompanyTotalCost !== undefined
                                    ? (data.ourCompanyTotalCost / 1000000).toLocaleString()
                                    : ''
                                }
                              >
                                {
                                  data.ourCompanyTotalCost !== null && data.ourCompanyTotalCost !== undefined
                                    ? (data.ourCompanyTotalCost / 1000000).toLocaleString()
                                    : ''
                                }
                              </span>
                            </>
                          : <></>
                      }
                    </>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={1}
                    colSpan={3}
                    row={4}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('constructionCd')
                      }}
                    >
                      {data.constructionCd}{' '}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('architectureUse')
                      }}
                    >
                      {data.architectureUse}{' '}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: 'gray'
                      }}
                    >
                      （
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('structureUnderGroundStr')
                      }}
                    >
                      {data.structureUnderGroundStr}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: 'gray'
                      }}
                    >
                      /{' '}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('structureGroundStr')
                      }}
                    >
                      {data.structureGroundStr}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: 'gray'
                      }}
                    >
                      ）
                    </span>
                    {
                      data.totalFloorAreaM2 !== null
                        ? <>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('totalFloorAreaM2'),
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden'
                              }}
                              title={data.totalFloorAreaM2?.toString() ?? ''}
                            >
                              {data.totalFloorAreaM2?.toLocaleString() ?? ''}
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: 'gray'
                              }}
                            >
                              ㎡/
                            </span>
                          </>
                        : <></>
                    }
                    {
                      data.totalFloorAreaTsubo !== null
                        ? <>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('totalFloorAreaTsubo'),
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden'
                              }}
                              title={data.totalFloorAreaTsubo?.toString() ?? ''}
                            >
                              {data.totalFloorAreaTsubo?.toLocaleString() ?? ''}
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: 'gray'
                              }}
                            >
                              坪
                            </span>
                          </>
                        : <></>
                    }
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: 'gray'
                      }}
                    >
                      -
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: 'gray'
                      }}
                    >
                      （
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('floorUnderGround')
                      }}
                    >
                      {data.floorUnderGround}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: 'gray'
                      }}
                    >
                      /
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('floorGround')
                      }}
                    >
                      {data.floorGround}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: 'gray'
                      }}
                    >
                      /
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('floorPenthouse')
                      }}
                    >
                      {data.floorPenthouse}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: 'gray'
                      }}
                    >
                      ）
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={1}
                    colSpan={3}
                    row={5}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    {
                      data.buildingServicesWorkFlg !== null
                        ? <>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: 'gray'
                              }}
                            >
                              設備工事:
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('buildingServicesWorkFlg')
                              }}
                            >
                              {convertFlagValue(
                                'buildingServicesWorkFlg',
                                data.buildingServicesWorkFlg
                              )}{' '}
                            </span>
                          </>
                        : <></>
                    }
                    {
                      data.piling !== null
                        ? <>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: 'gray'
                              }}
                            >
                              杭打：
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('piling')
                              }}
                            >
                              {convertFlagValue('piling', data.piling)}{' '}
                            </span>
                          </>
                        : <></>
                    }
                    {
                      data.seismicIsolation !== null
                        ? <>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: 'gray'
                              }}
                            >
                              免震：
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('seismicIsolation')
                              }}
                            >
                              {convertFlagValue(
                                'seismicIsolation',
                                data.seismicIsolation
                              )}{' '}
                            </span>
                          </>
                        : <></>
                    }
                    {
                      data.topDown !== null
                        ? <>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('topDown')
                              }}
                            >
                              {convertFlagValue('topDown', data.topDown)}{' '}
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: 'gray'
                              }}
                            >
                              工法：
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('specialConstructionMethod'),
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden'
                              }}
                              title={data.specialConstructionMethod ?? ''}
                            >
                              {data.specialConstructionMethod}
                            </span>
                          </>
                        : <></>
                    }
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={1}
                    colSpan={3}
                    row={6}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('startYM')
                      }}
                    >
                      {(data.startYM != null && data.startYM.length > 0) ? dayjs(data.startYM).format('YYYY/MM') : ''}
                    </span>
                    -
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('completionYM')
                      }}
                    >
                      {(data.completionYM != null && data.completionYM.length > 0) ? dayjs(data.completionYM).format('YYYY/MM') : ''}
                    </span>
                    {
                      data.schedule !== null
                        ? <>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('schedule')
                              }}
                            >
                              {'                '}{data.schedule ?? ''}
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: 'gray'
                              }}
                            >
                              ヶ月
                            </span>
                          </>
                        : <></>
                    }
                    {
                      data.siteAreaM2 !== null
                        ? <>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('siteAreaM2'),
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden'
                              }}
                              title={data.siteAreaM2?.toString() ?? ''}
                            >
                              {'                '}{data.siteAreaM2?.toLocaleString() ?? ''}
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: 'gray'
                              }}
                            >
                              ㎡/
                            </span>
                          </>
                        : <></>
                    }
                    {
                      data.siteAreaTsubo !== null
                        ? <>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: getItemColor('siteAreaTsubo'),
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden'
                              }}
                              title={data.siteAreaTsubo?.toString() ?? ''}
                            >
                              {data.siteAreaTsubo?.toLocaleString() ?? ''}
                            </span>
                            <span
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                                color: 'gray'
                              }}
                            >
                              坪
                            </span>
                          </>
                        : <></>
                    }
                    {
                      data.sagyosyoCd !== null
                        ? <span
                            style={{
                              fontWeight: 'bold',
                              fontSize: '0.8em',
                              color: getItemColor('sagyosyoCd'),
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden'
                            }}
                            title={data.sagyosyoCd?.toString() ?? ''}
                          >
                            {'                '}{data.sagyosyoCd?.toLocaleString() ?? ''}
                          </span>
                        : <></>
                    }
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={1}
                    colSpan={3}
                    row={7}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <>
                      <span
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          color: 'gray'
                        }}
                      >
                        備考：
                      </span>
                      <span
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          color: getItemColor('remark'),
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden'
                        }}
                        title={data.remark ?? ''}
                      >
                        {data.remark}
                      </span>
                    </>
                  </GridLayoutItem>
                </GridLayout>
              </GridLayoutItem>
              <GridLayoutItem col={2}>
                <GridLayout
                  gap={{ cols: 1 }}
                  rows={[
                    { height: '1.0em' },
                    { height: '1.0em' },
                    { height: '1.0em' },
                    { height: '1.0em' },
                    { height: '1.0em' },
                    { height: '1.0em' },
                    { height: '1.0em' },
                    { height: '1.0em' },
                    { height: '1.0em' }
                  ]}
                  cols={[{ width: '30%' }, { width: '70%' }]}
                >
                  <GridLayoutItem col={1} row={1}>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: 'gray',
                        verticalAlign: 'top'
                      }}>
                        工事部
                      </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={2}
                    row={1}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('constructionSectionNm'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.constructionSectionNm ?? ''}
                    >
                      {data.constructionSectionNm}
                    </span>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: 'gray',
                        verticalAlign: 'top'
                      }}
                    >
                      ）
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        color: getItemColor('chiefPersonNm'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.chiefPersonNm ?? ''}
                    >
                      {data.chiefPersonNm}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={2}
                    row={2}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('chiefPersonContacts'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.chiefPersonContacts ?? ''}
                    >
                      {data.chiefPersonContacts}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem col={1} row={3}>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: 'gray',
                        verticalAlign: 'top'
                      }}
                    >
                      本社営業部
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={2}
                    row={3}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('hqSalesSectionNm'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.hqSalesSectionNm ?? ''}
                    >
                      {data.hqSalesSectionNm}
                    </span>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: 'gray',
                        verticalAlign: 'top'
                      }}
                    >
                      ）
                    </span>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('hqSalesPersonNm'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.hqSalesPersonNm ?? ''}
                    >
                      {data.hqSalesPersonNm}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={2}
                    row={4}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('hqSalesPersonContacts'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.hqSalesPersonContacts ?? ''}
                    >
                      {data.hqSalesPersonContacts}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem col={1} row={5}>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: 'gray',
                        verticalAlign: 'top'
                      }}
                    >
                      支店営業部
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={2}
                    row={5}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('branchSalesSectionNm'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.branchSalesSectionNm ?? ''}
                    >
                      {data.branchSalesSectionNm}
                    </span>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: 'gray',
                        verticalAlign: 'top'
                      }}
                    >
                      ）
                    </span>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('branchSalesPersonNm'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.branchSalesPersonNm ?? ''}
                    >
                      {data.branchSalesPersonNm}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={2}
                    row={6}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('branchSalesPersonContacts'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.branchSalesPersonContacts ?? ''}
                    >
                      {data.branchSalesPersonContacts}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem col={1} row={7}>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: 'gray',
                        verticalAlign: 'top'
                      }}
                    >
                      発注者
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={2}
                    row={7}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('customerNm'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.customerNm ?? ''}
                    >
                      {data.customerNm}
                    </span>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: 'gray',
                        verticalAlign: 'top'
                      }}
                    >
                      {' '}
                    </span>
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: `${getItemColor('collabCustomerNm')}`,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.collabCustomerNm ?? ''}
                    >
                      {data.collabCustomerNm}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem col={1} row={8}>
                    <span style={{ fontSize: '0.8em', color: 'gray' }}>設計</span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={2}
                    row={8}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('designKbnNm'),
                        verticalAlign: 'top'
                      }}
                    >
                      {data.designKbnNm}
                    </span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={2}
                    row={9}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8em',
                        color: getItemColor('designerNm'),
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        verticalAlign: 'top'
                      }}
                      title={data.designerNm ?? ''}
                    >
                      {data.designerNm}
                    </span>
                  </GridLayoutItem>
                </GridLayout>
              </GridLayoutItem>

              <GridLayoutItem col={3}>
                <GridLayoutItem col={1} colSpan={3} row={1}>
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: '0.8em',
                      color: 'gray',
                      verticalAlign: 'top'
                    }}
                  >
                    修正コメント：
                  </span>
                </GridLayoutItem>
                <GridLayoutItem
                  col={1}
                  colSpan={3}
                  row={2}
                  style={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    color: `${getItemColor('fixComment')}`
                  }}
                >
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: '0.8em',
                      color: getItemColor('fixComment'),
                      verticalAlign: 'top'
                    }}
                    title={data.fixComment ?? ''}
                  >
                    {data.fixComment}
                  </span>
                </GridLayoutItem>
              </GridLayoutItem>
            </GridLayout>
          </CardBody>
        </Card>
      </Tooltip>
    </>
  )
}
